// Pre-suspend summary for a Channel Partner. Shown after the existing
// "Suspend Channel Partner?" ConfirmationAlert and before the suspend itself.
//
// A bespoke modal rather than an extension of Subcomponents/ConfirmationAlert:
// that component takes only title/subTitle strings, has no content slot, and is
// used in many other places -- adding a textarea and a status list to it would
// put every other caller at risk.
//
// Three checks run in parallel on open:
//   site-visit count   informational, never blocks
//   allotment count    informational, never blocks
//   CP payment status  BLOCKS unless every non-deleted row is a settled status
// plus a mandatory note. Confirm stays disabled until the payment check passes
// and the note is non-empty.
import React, { useState, useEffect, useCallback } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import { makeStyles } from "@mui/styles";
import cssClasses from "./SuspendCpSummary.module.css";
import Close from "../../Components/Icons/Close";
import Button from "../../Subcomponents/Button";
import {
  getCpSiteVisitCount,
  getCpAllotmentCount,
  getCpPaymentSuspendCheck,
} from "../../Services/ChannelPartner";

const NOTE_MAX_LENGTH = 500;

const useStyles = makeStyles((theme) => ({
  wrapperIcons: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-around",
    marginBottom: "2rem",
    position: "relative",
  },
  closeIcon: {
    cursor: "pointer",
    position: "absolute",
    right: "0rem",
  },
  title: {
    fontFamily: "Inter-Medium",
    fontStyle: "normal",
    fontWeight: "500",
    fontSize: "2.4rem",
    lineHeight: "3.2rem",
    color: "#101828",
    marginBottom: "0.8rem",
  },
  subTitle: {
    fontFamily: "Inter-Medium",
    fontStyle: "normal",
    fontWeight: "500",
    fontSize: "1.6rem",
    lineHeight: "2.4rem",
    color: "#667085",
    marginBottom: "2.4rem",
  },
  loadingWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    columnGap: "1.2rem",
    padding: "3.2rem 0",
    fontFamily: "Inter-Medium",
    fontSize: "1.6rem",
    color: "#667085",
  },
  section: {
    marginBottom: "2rem",
  },
  sectionLabel: {
    fontFamily: "Inter-Medium",
    fontSize: "1.2rem",
    lineHeight: "1.8rem",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#98A2B3",
    marginBottom: "0.8rem",
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0.8rem 1.2rem",
    background: "#F9FAFB",
    borderRadius: "0.8rem",
    marginBottom: "0.4rem",
    fontFamily: "Inter-Medium",
    fontSize: "1.5rem",
    lineHeight: "2.2rem",
    color: "#344054",
  },
  infoValue: {
    color: "#101828",
    fontWeight: 600,
  },
  hint: {
    fontFamily: "Inter-Medium",
    fontSize: "1.3rem",
    lineHeight: "1.9rem",
    color: "#98A2B3",
    marginTop: "0.6rem",
  },
  statusList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.6rem",
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "0.6rem",
    padding: "0.4rem 1rem",
    borderRadius: "1.6rem",
    fontFamily: "Inter-Medium",
    fontSize: "1.4rem",
    lineHeight: "2rem",
    border: "1px solid",
  },
  statusPillOk: {
    color: "#027A48",
    background: "#ECFDF3",
    borderColor: "#ABEFC6",
  },
  statusPillBlocking: {
    color: "#B42318",
    background: "#FEF3F2",
    borderColor: "#FECDCA",
  },
  blockBox: {
    marginTop: "1.2rem",
    padding: "1.2rem",
    background: "#FEF3F2",
    border: "1px solid #FECDCA",
    borderRadius: "0.8rem",
    fontFamily: "Inter-Medium",
    fontSize: "1.4rem",
    lineHeight: "2.1rem",
    color: "#B42318",
  },
  errorBox: {
    padding: "1.2rem",
    background: "#FEF3F2",
    border: "1px solid #FECDCA",
    borderRadius: "0.8rem",
    fontFamily: "Inter-Medium",
    fontSize: "1.4rem",
    lineHeight: "2.1rem",
    color: "#B42318",
    marginBottom: "2rem",
  },
  noteLabelRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    marginBottom: "0.8rem",
  },
  noteLabel: {
    fontFamily: "Inter-Medium",
    fontSize: "1.5rem",
    lineHeight: "2.2rem",
    color: "#344054",
  },
  required: {
    color: "#B42318",
  },
  counter: {
    fontFamily: "Inter-Medium",
    fontSize: "1.3rem",
    color: "#98A2B3",
  },
  TextFieldStyle: {
    width: "100%",
    background: "#F2F4F7 !important",
    paddingRight: "0rem !important",
    borderRadius: "10px !important",
    fontSize: "1.6rem !important",
    fontFamily: "Inter-Medium !important",
    lineHeight: "2.4rem !important",
  },
  inputStyle: {
    fontFamily: "Inter-Medium !important",
    paddingRight: "0rem !important",
    fontSize: "1.6rem !important",
    lineHeight: "2.4rem !important",
    background: "#F2F4F7  !important",
    boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
    borderRadius: "10px !important",
    border: "1px solid #F2F4F7",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&.Mui-focused": {
      background: "#FFFFFF !important",
      border: "1px solid #84CAFF !important",
      boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px #D1E9FF",
    },
  },
  wrapperButtons: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "1.2rem",
    marginTop: "2.4rem",
  },
}));

export default function SuspendCpSummary({
  open,
  handleClose,
  cp,
  onConfirm,
}) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [siteVisitCount, setSiteVisitCount] = useState(null);
  const [allotmentCount, setAllotmentCount] = useState(null);
  const [payment, setPayment] = useState(null);
  const [note, setNote] = useState("");

  const cpId = cp?.id;

  const runChecks = useCallback(() => {
    setLoading(true);
    setLoadError("");
    // allSettled, not all: the two counts are informational and must never stop
    // a suspend, so if one of them fails we show it as unavailable and carry on.
    // The payment check is the actual gate -- if it fails we cannot evaluate the
    // rule, so Confirm stays blocked rather than failing open.
    Promise.allSettled([
      getCpSiteVisitCount(cpId),
      getCpAllotmentCount(cpId),
      getCpPaymentSuspendCheck(cpId),
    ])
      .then(([visits, allotments, paymentCheck]) => {
        setSiteVisitCount(visits.status === "fulfilled" ? visits.value : null);
        setAllotmentCount(allotments.status === "fulfilled" ? allotments.value : null);
        if (paymentCheck.status === "fulfilled") {
          setPayment(paymentCheck.value);
        } else {
          setPayment(null);
          console.error("getCpPaymentSuspendCheck", paymentCheck.reason);
          setLoadError(
            "Could not read this CP's payment status, so the suspend cannot be verified. Close this window and try again."
          );
        }
      })
      .finally(() => setLoading(false));
  }, [cpId]);

  // Re-run whenever the modal opens, and reset the note so a reason typed for
  // one CP can never be carried over to another.
  useEffect(() => {
    if (!open || !cpId) return;
    setNote("");
    setSiteVisitCount(null);
    setAllotmentCount(null);
    setPayment(null);
    runChecks();
  }, [open, cpId, runChecks]);

  const noteFilled = note.trim() !== "";
  const paymentAllows = payment?.canSuspend === true;
  const confirmDisabled = loading || !!loadError || !paymentAllows || !noteFilled;

  function blockedReason() {
    if (loading) return "";
    if (loadError) return "";
    if (!paymentAllows && payment) {
      const blockingCount = payment.blocking.reduce((sum, entry) => sum + entry.count, 0);
      if (blockingCount === 1) {
        return `This CP has 1 unsettled CP payment entry (${payment.blocking[0].status}). Delete it from CP Payout before suspending this CP.`;
      }
      const names = payment.blocking
        .map((entry) => `${entry.count} ${entry.status}`)
        .join(", ");
      return `This CP has ${blockingCount} unsettled CP payment entries (${names}). Delete them from CP Payout before suspending this CP.`;
    }
    return "";
  }

  function renderCount(value) {
    return value === null ? "Unavailable" : value;
  }

  return (
    <Modal
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="suspend-cp-summary-title"
      sx={[{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }]}
      disableAutoFocus={true}
    >
      <Box className={cssClasses.modalWrapper}>
        <div className={classes.wrapperIcons}>
          <span className={classes.closeIcon} onClick={handleClose}>
            <Close width={"1.2rem"} height={"1.2rem"} />
          </span>
        </div>

        <div className={classes.title} id="suspend-cp-summary-title">
          Suspend {cp?.billingName || "this CP"}?
        </div>

        {loading ? (
          <div className={classes.loadingWrap}>
            <CircularProgress size={20} />
            <span>Running checks…</span>
          </div>
        ) : (
          <>
            {loadError !== "" && <div className={classes.errorBox}>{loadError}</div>}

            <div className={classes.section}>
              <div className={classes.sectionLabel}>Activity on this CP&apos;s leads</div>
              <div className={classes.infoRow}>
                <span>Site visits</span>
                <span className={classes.infoValue}>{renderCount(siteVisitCount)}</span>
              </div>
              <div className={classes.infoRow}>
                <span>Allotments</span>
                <span className={classes.infoValue}>{renderCount(allotmentCount)}</span>
              </div>
            </div>

            <div className={classes.section}>
              <div className={classes.sectionLabel}>CP payment status</div>
              {payment === null ? (
                <div className={classes.hint}>Unavailable.</div>
              ) : payment.total === 0 ? (
                <div className={classes.hint}>No CP payment entries for this CP.</div>
              ) : (
                <div className={classes.statusList}>
                  {payment.summary.map((entry) => (
                    <span
                      key={entry.status}
                      className={`${classes.statusPill} ${
                        entry.blocks ? classes.statusPillBlocking : classes.statusPillOk
                      }`}
                    >
                      {entry.count} {entry.status}
                    </span>
                  ))}
                </div>
              )}
              {blockedReason() !== "" && (
                <div className={classes.blockBox}>{blockedReason()}</div>
              )}
            </div>

            <div className={classes.section}>
              <div className={classes.noteLabelRow}>
                <span className={classes.noteLabel}>
                  Reason for suspending <span className={classes.required}>*</span>
                </span>
                <span className={classes.counter}>
                  {note.length}/{NOTE_MAX_LENGTH}
                </span>
              </div>
              <TextField
                value={note}
                onChange={(e) => {
                  const next = e.target.value.trimStart();
                  if (next.length <= NOTE_MAX_LENGTH) {
                    setNote(next);
                  }
                }}
                placeholder={"Why is this CP being suspended?"}
                className={classes.TextFieldStyle}
                InputProps={{ className: classes.inputStyle }}
                minRows={3}
                maxRows={6}
                multiline
              />
            </div>
          </>
        )}

        <div className={classes.wrapperButtons}>
          <Button
            style={{
              color: "#344054",
              backgroundColor: "#ffffff",
              fontSize: "1.6rem",
              lineHeight: "2.4rem",
            }}
            btnName={"Cancel"}
            clicked={handleClose}
          />
          <Button
            style={{
              color: "#ffffff",
              // Muted red while disabled -- Subcomponents/Button only drops the
              // opacity to 0.7 when disabled, which reads as enabled at a glance
              // on a destructive action.
              backgroundColor: confirmDisabled ? "#FDA29B" : "#D92D20",
              fontSize: "1.6rem",
              lineHeight: "2.4rem",
            }}
            btnName={"Suspend"}
            // Guarded here as well as via `disable`: Subcomponents/Button blocks a
            // disabled click with pointerEvents:none, which stops the mouse but
            // not Tab+Enter, and this action is irreversible.
            clicked={() => {
              if (confirmDisabled) return;
              onConfirm(note.trim());
            }}
            disable={confirmDisabled}
            tabIndex={confirmDisabled ? -1 : 0}
          />
        </div>
      </Box>
    </Modal>
  );
}
